import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as BackButton } from "../img/bacbuttonicon.svg";
import { instance2 } from "../utils/axiosInstance";
import DatePicker from "react-datepicker";
import CalendarIcon from "../img/calendaricon.svg";

export default function TambahhariLibur() {
  const navigate = useNavigate();
  const location = useLocation();

  const localDateString = (thedate) => {
    const year = thedate.getFullYear();
    const month = String(thedate.getMonth() + 1).padStart(2, '0'); // Adding 1 because months are 0-based
    const day = String(thedate.getDate()).padStart(2, '0');
  
    return `${year}-${month}-${day}`;
  };
  
  // Extract holidayId from the URL
  const queryParams = new URLSearchParams(location.search);
  const holidayId = queryParams.get("id");

  // State
  const [loading, setLoading] = useState(!!holidayId); // Load only if editing
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    date: localDateString(new Date()),
    name: "",
    description: "",
    admin_id: "",
  });

  // Get user ID from local storage
  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    if (storedUserId) {
      setUserId(parseInt(storedUserId, 10));
      setFormData((prev) => ({
        ...prev,
        admin_id: parseInt(storedUserId, 10),
      }));
    }
  }, []);

  // Fetch holiday details if editing (holidayId exists)
  useEffect(() => {
    if (holidayId) {
      setLoading(true);
      instance2
        .get(`holidays/id/${holidayId}`)
        .then((response) => {
          if (response.data && response.data.length > 0) {
            const holiday = response.data[0];
  
            // ✅ Safe date conversion
            const parsedDate = holiday.date ? new Date(Date.parse(holiday.date)) : new Date();
  
            setFormData({
              date: localDateString(parsedDate), // Convert to correct format
              name: holiday.name || "",
              description: holiday.description || "",
              admin_id: holiday.admin_id || userId,
            });
            
            setSelectedDate(parsedDate);
          }
        })
        .catch((err) => setError("Failed to fetch holiday data"))
        .finally(() => setLoading(false));
    }
  }, [holidayId, userId]);
  
  

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Validate form
  const isFormValid = () => {
    return formData.date && formData.name && formData.admin_id;
  };

  // Handle submit (POST for new, PUT for edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiUrl = holidayId ? `holidays/id/${holidayId}` : "holidays";
      const method = holidayId ? "put" : "post";
      const response = await instance2[method](apiUrl, formData);
      console.log("Success:", formData);
      navigate(-1); // Go back after success
    } catch (error) {
      console.error("Error:", error);
      setError("Failed to save data");
    }
  };

  // Custom Input for Date Picker
  const CustomInput = React.forwardRef(({ value, onClick, isOpen }, ref) => (
    <button
      className="flex items-center justify-between w-full px-4 py-3 border rounded-full bg-white"
      onClick={onClick}
      ref={ref}
      type="button"
    >
      <div className="flex items-center gap-2">
        <img src={CalendarIcon} alt="Calendar" className="h-5 w-5 text-gray-500" />
        <div className="w-px h-5 bg-gray-300 mx-1 me-3"></div>
        <span>
          {new Date(value).toLocaleDateString("id-ID", {
            year: "numeric",
            month: "long",
            day: "2-digit",
          })}
        </span>
      </div>
      <div
        className={`text-black font-semibold transform transition-transform duration-500 ease-in-out ${
          isOpen ? "rotate-180" : "rotate-0"
        }`}
      >
        ▼
      </div>
    </button>
  ));

  return (
    <div className="p-4">
      <div className="flex flex-row gap-4 items-center">
        <div className="bg-white p-2 rounded-full cursor-pointer" onClick={() => navigate(-1)}>
          <BackButton className="rotate-180 text-black" />
        </div>
        <h1 className="block text-black text-xl sm:text-2xl my-2 font-medium">
          {holidayId ? "Ubah Hari Libur" : "Tambah Hari Libur"}
        </h1>
      </div>

      {loading ? (
        <div className="px-4 py-8 flex justify-center">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
        </div>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <div className="py-5">
          {/* Form Section */}
          <div className="flex flex-col gap-6 lg:flex-row justify-between items-center">
            {/* Left Column */}
            <div className="flex flex-col gap-4 w-full lg:w-1/2">
              <div className="flex flex-col sm:flex-row gap-4 items-center">
                <label className="block text-gray-700 w-full sm:w-1/4 text-sm">Tanggal</label>
                <div className="flex flex-row w-full items-center justify-start">
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date) => {
                      setSelectedDate(date);
                      setFormData((prev) => ({
                        ...prev,
                        date: localDateString(date),
                      }));
                    }}
                    customInput={
                      <CustomInput
                        value={selectedDate.toLocaleDateString("id-ID", {
                          year: "numeric",
                          month: "long",
                          day: "2-digit",
                        })}
                        isOpen={isOpen}
                      />
                    }
                    onCalendarOpen={() => setIsOpen(true)}
                    onCalendarClose={() => setIsOpen(false)}
                    dateFormat="yyyy-MM-dd"
                    filterDate={(date) => {
                      const day = date.getDay();
                      return day !== 0 && day !== 6; // Disable weekends
                    }}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 items-center">
                <label className="block text-gray-700 w-full sm:w-1/4 text-sm">Nama Libur</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 border-none rounded-[50px] bg-white text-xs py-4"
                  placeholder="Hari Libur"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 items-center">
                <label className="block text-gray-700 w-full sm:w-1/4 text-sm">Keterangan</label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full px-3 border-none rounded-[50px] bg-white text-xs py-4"
                  placeholder="Keterangan"
                />
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="w-1/2 flex flex-col sm:flex-row justify-center mt-4 px-4 sm:px-8 gap-5">
            <button className="bg-merah-no py-5 px-8 text-white rounded-[30px]" onClick={() => navigate(-1)}>
              Kembali
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isFormValid()}
              className={`py-5 px-8 rounded-[30px] text-sm ${
                isFormValid() ? "bg-merah-no text-white hover:bg-merah-hover" : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              {holidayId ? "Update Hari Libur" : "Tambah Hari Libur"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
